/* Open maps directly; the HTTPS link also works without JavaScript. */
(() => {
  if (customElements.get('unik-store-directions')) return;

  class UnikStoreDirections extends HTMLElement {
    connectedCallback() {
      const link = this.querySelector('a');
      const query = this.dataset.mapQuery;
      if (!link || !query) return;

      const ua = navigator.userAgent;
      const fallback = `https://www.google.com/maps/dir/?api=1&destination=${query}`;
      if (/Android/.test(ua)) {
        const androidChrome = /Chrome\//.test(ua) &&
          !/; wv\)|Version\/|EdgA\/|OPR\/|SamsungBrowser\//.test(ua);
        // No package: let Android resolve the default map app. Chrome can open
        // the web map directly if no installed app handles the generic geo URI.
        link.href = androidChrome
          ? `intent:0,0?q=${query}#Intent;scheme=geo;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${encodeURIComponent(fallback)};end`
          : `geo:0,0?q=${query}`;
      } else if (/iPhone|iPad|iPod|Macintosh/.test(ua)) {
        // Apple map links hand off to Maps where supported. Availability of a
        // different default navigation app depends on Apple's OS/region rules.
        link.href = `https://maps.apple.com/?daddr=${query}`;
      } else {
        link.href = fallback;
      }
    }
  }

  customElements.define('unik-store-directions', UnikStoreDirections);
})();

/* Decorative background: no visible or native controls. */
(() => {
  if (customElements.get('unik-store-video')) return;

  class UnikStoreVideo extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.template = this.querySelector('template');
      if (!this.template || !('IntersectionObserver' in window)) return;
      try {
        this.sources = JSON.parse(this.dataset.sources || '[]')
          .filter(source => source.url && source.width && source.height && ['mp4', 'm3u8'].includes(source.format))
          .sort((a, b) => a.width - b.width);
      } catch (_) {
        return;
      }
      if (!this.sources.some(source => source.format === 'mp4')) return;
      this.cleanup = [];
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.visible = false;
      this.near = false;
      this.pending = null;
      this.blocked = false;
      this.failed = false;
      this.hasFrame = false;
      this.abortRetries = 0;
      this.networkRetried = false;
      this.hlsFallbackTried = false;
      this.listen(document, 'visibilitychange', () => this.reconcile());
      this.listen(window, 'pageshow', () => this.reconcile());
      this.listen(this.reduced, 'change', () => this.reconcile());
      if (this.connection) this.listen(this.connection, 'change', () => this.reconcile());
      this.listen(window, 'online', () => {
        // Retry a network failure once when connectivity actually returns.
        if (this.failed && this.video?.error?.code === 2 && !this.networkRetried) {
          this.networkRetried = true;
          this.failed = false;
          this.video.load();
          this.reconcile();
        }
      });
      this.listen(document, 'shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.abortRetries = 0;
        this.reconcile();
      }, { threshold: 0 });
      this.observer.observe(this);
      // Buffer shortly before entry; no src or video request elsewhere on the page.
      this.loadObserver = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        this.prepare();
      }, { rootMargin: '600px 0px', threshold: 0 });
      this.loadObserver.observe(this);
      this.sync();
    }

    listen(target, event, handler) {
      target.addEventListener(event, handler);
      this.cleanup.push(() => target.removeEventListener(event, handler));
    }

    enabled() {
      return this.dataset.autoplay === 'true' && !this.reduced.matches && !this.connection?.saveData;
    }

    shouldPlay() {
      return this.isConnected && this.visible && !document.hidden && this.enabled() && !this.blocked && !this.failed;
    }

    prepare() {
      if (this.near && !document.hidden && this.enabled() && !this.blocked && !this.failed && !this.video) {
        this.createVideo();
      }
    }

    reconcile() {
      if (!this.shouldPlay()) {
        this.stop();
        this.sync();
        return;
      }
      this.start();
    }

    createVideo() {
      if (this.video) return;
      // Video detail is masked by the overlay: avoid retina-sized downloads.
      // Honor cover's height and choose a smaller available rendition on slow links.
      const mp4Sources = this.sources.filter(source => source.format === 'mp4');
      const ratio = mp4Sources[0].width / mp4Sources[0].height;
      const slow = /(^|-)2g$/.test(this.connection?.effectiveType || '') ||
        (this.connection?.downlink > 0 && this.connection.downlink < 2);
      const targetWidth = Math.min(Math.max(this.clientWidth, this.clientHeight * ratio), slow ? 854 : 1920);
      this.mp4Source = mp4Sources.find(item => item.width >= targetWidth) || mp4Sources[mp4Sources.length - 1];
      const video = this.template.content.firstElementChild.cloneNode(true);
      this.video = video;
      // Prefer adaptive delivery where the browser supports it natively. The
      // current film only exposes a large 1080p MP4. No external player library.
      const hlsSource = this.sources.find(source => source.format === 'm3u8');
      const source = hlsSource && video.canPlayType('application/vnd.apple.mpegurl') ? hlsSource : this.mp4Source;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.controls = false;
      video.preload = 'auto';
      this.listen(video, 'playing', () => {
        if (!this.shouldPlay()) {
          this.stop();
          return;
        }
        // Keep the poster until a decoded frame is presented, not just play().
        if (video.requestVideoFrameCallback) {
          this.frameCallback = video.requestVideoFrameCallback(() => {
            this.frameCallback = null;
            if (this.video !== video || !this.shouldPlay()) return;
            this.hasFrame = true;
            this.sync();
          });
        } else {
          this.hasFrame = video.readyState >= 2;
          this.sync();
        }
      });
      this.listen(video, 'pause', () => this.sync());
      this.listen(video, 'error', () => {
        if (this.dataset.sourceFormat === 'm3u8' && !this.hlsFallbackTried) {
          this.hlsFallbackTried = true;
          this.hasFrame = false;
          this.stop();
          this.setSource(this.mp4Source);
          this.reconcile();
          this.sync();
          return;
        }
        this.failed = true;
        this.hasFrame = false;
        this.stop();
        this.sync();
      });
      // A single source assignment starts preload. Do not reset it with load()
      // around play(): that discards buffers and can reject pending play promises.
      this.querySelector('.unik-store__media').append(video);
      this.setSource(source);
    }

    setSource(source) {
      this.video.src = source.url.startsWith('//') ? `https:${source.url}` : source.url;
      this.dataset.sourceWidth = source.width;
      this.dataset.sourceHeight = source.height;
      this.dataset.sourceFormat = source.format;
    }

    start() {
      if (!this.shouldPlay()) return;
      this.createVideo();
      const video = this.video;
      if (this.pending || !video.paused) return;
      const attempt = { video };
      this.pending = attempt;
      try {
        Promise.resolve(video.play()).then(() => {
          if (this.pending !== attempt) return;
          this.pending = null;
          if (!this.shouldPlay()) this.stop();
          this.sync();
        }).catch(error => this.rejected(attempt, error));
      } catch (error) {
        this.rejected(attempt, error);
      }
    }

    rejected(attempt, error) {
      // Ignore canceled attempts after scroll, tab changes or editor removal.
      if (this.pending !== attempt || this.video !== attempt.video) return;
      this.pending = null;
      if (error.name === 'AbortError') {
        this.stop();
        // An interruption is not an autoplay-policy denial. One bounded retry
        // handles browser races without continuously retrying blocked autoplay.
        if (this.shouldPlay() && this.abortRetries++ < 1) {
          this.retryTimer = setTimeout(() => { this.retryTimer = null; this.reconcile(); }, 250);
        }
      } else {
        this.blocked = true;
        this.hasFrame = false;
        this.stop();
      }
      this.sync();
    }

    stop() {
      this.pending = null;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      if (this.frameCallback != null) this.video?.cancelVideoFrameCallback?.(this.frameCallback);
      this.frameCallback = null;
      this.video?.pause();
    }

    sync() {
      // Retain the decoded still frame through pause/re-entry and buffering.
      // Reduced motion, disabled autoplay, policy denial and errors show poster.
      this.toggleAttribute('data-has-frame', this.hasFrame && this.enabled() && !this.blocked && !this.failed);
      this.toggleAttribute('data-playing', !!this.video && !this.video.paused && this.shouldPlay() && this.hasFrame);
    }

    disconnectedCallback() {
      if (!this.cleanup) return;
      this.stop();
      this.observer.disconnect();
      this.loadObserver.disconnect();
      this.cleanup.forEach(remove => remove());
      this.cleanup = null;
      if (this.video) {
        this.video.removeAttribute('src');
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      this.hasFrame = false;
      this.removeAttribute('data-has-frame');
      this.removeAttribute('data-playing');
    }
  }

  customElements.define('unik-store-video', UnikStoreVideo);
})();
